var __defProp = Object.defineProperty;
var __export = (target, all) => {
  for (var name in all)
    __defProp(target, name, { get: all[name], enumerable: true });
};

// src/schemas/patient.schema.ts
import { z } from "zod";
var patientStatusEnum = z.enum(["active", "inactive", "discharged", "deleted"]);
var patientSchema = z.object({
  id: z.string().uuid(),
  full_name: z.string().min(1, "Nome \xE9 obrigat\xF3rio"),
  birth_date: z.string().datetime().nullable(),
  address: z.string().nullable(),
  emergency_contact: z.string().nullable(),
  emergency_contact_country_code: z.string().nullable().default("55"),
  condition_description: z.string().nullable(),
  medical_history: z.string().nullable(),
  current_medications: z.array(z.string()).nullable(),
  status: patientStatusEnum.default("active"),
  organization_id: z.string().uuid(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime()
});
var createPatientSchema = z.object({
  full_name: z.string().min(1, "Nome \xE9 obrigat\xF3rio"),
  birth_date: z.string().datetime().nullable().optional(),
  address: z.string().nullable().optional(),
  emergency_contact: z.string().nullable().optional(),
  emergency_contact_country_code: z.string().nullable().optional(),
  condition_description: z.string().nullable().optional(),
  medical_history: z.string().nullable().optional(),
  current_medications: z.array(z.string()).nullable().optional(),
  organization_id: z.string().uuid()
});
var updatePatientSchema = z.object({
  full_name: z.string().min(1, "Nome e obrigatorio").optional(),
  birth_date: z.string().datetime().nullable().optional(),
  address: z.string().nullable().optional(),
  emergency_contact: z.string().nullable().optional(),
  emergency_contact_country_code: z.string().nullable().optional(),
  condition_description: z.string().nullable().optional(),
  medical_history: z.string().nullable().optional(),
  current_medications: z.array(z.string()).nullable().optional(),
  status: patientStatusEnum.optional()
});

// src/schemas/family-member.schema.ts
import { z as z2 } from "zod";
var familyRelationshipEnum = z2.enum([
  "mother",
  "father",
  "grandparent",
  "sibling",
  "uncle_aunt",
  "caregiver",
  "other"
]);
var familyMemberSchema = z2.object({
  id: z2.string().uuid(),
  family_id: z2.string().uuid(),
  patient_id: z2.string().uuid(),
  name: z2.string().min(1, "Nome \xE9 obrigat\xF3rio"),
  relationship: familyRelationshipEnum,
  phone: z2.string().nullable(),
  email: z2.string().email().nullable(),
  is_primary_contact: z2.boolean().default(false),
  accepts_notifications: z2.boolean().default(false),
  notification_opt_out_source: z2.string().nullable().optional(),
  created_at: z2.string().datetime(),
  updated_at: z2.string().datetime()
});
var createFamilyMemberSchema = z2.object({
  family_id: z2.string().uuid(),
  patient_id: z2.string().uuid(),
  name: z2.string().min(1, "Nome \xE9 obrigat\xF3rio"),
  relationship: familyRelationshipEnum,
  phone: z2.string().nullable().optional(),
  email: z2.string().email().nullable().optional(),
  is_primary_contact: z2.boolean().default(false),
  accepts_notifications: z2.boolean().default(false),
  notification_opt_out_source: z2.string().nullable().optional()
});

// src/schemas/report.schema.ts
import { z as z3 } from "zod";
var reportTypeEnum = z3.enum(["daily", "weekly_summary"]);
var sentimentEnum = z3.enum(["positive", "neutral", "negative", "mixed"]);
var dailyReportSchema = z3.object({
  id: z3.string().uuid(),
  patient_id: z3.string().uuid(),
  family_id: z3.string().uuid(),
  organization_id: z3.string().uuid(),
  type: reportTypeEnum.default("daily"),
  content: z3.string().min(1, "Conteudo e obrigatorio"),
  raw_message: z3.string().nullable(),
  sentiment: sentimentEnum.nullable(),
  ai_analysis: z3.record(z3.unknown()).nullable(),
  received_at: z3.string().datetime(),
  created_at: z3.string().datetime(),
  updated_at: z3.string().datetime()
});
var weeklySummarySchema = z3.object({
  id: z3.string().uuid(),
  patient_id: z3.string().uuid(),
  organization_id: z3.string().uuid(),
  start_date: z3.string().datetime(),
  end_date: z3.string().datetime(),
  summary: z3.string(),
  highlights: z3.array(z3.string()),
  concerns: z3.array(z3.string()),
  recommendations: z3.array(z3.string()),
  reports_count: z3.number().int(),
  created_at: z3.string().datetime()
});
var createReportSchema = z3.object({
  patient_id: z3.string().uuid(),
  family_id: z3.string().uuid(),
  organization_id: z3.string().uuid(),
  content: z3.string().min(1, "Conteudo e obrigatorio"),
  raw_message: z3.string().nullable().optional()
});

// src/schemas/insight.schema.ts
import { z as z4 } from "zod";
var insightSeverityEnum = z4.enum(["low", "medium", "high", "critical"]);
var insightStatusEnum = z4.enum(["pending", "acknowledged", "dismissed"]);
var keyInsightSchema = z4.object({
  id: z4.string().uuid(),
  patient_id: z4.string().uuid(),
  organization_id: z4.string().uuid(),
  report_id: z4.string().uuid().nullable(),
  summary_id: z4.string().uuid().nullable(),
  title: z4.string().min(1, "Titulo e obrigatorio"),
  description: z4.string(),
  severity: insightSeverityEnum,
  confidence: z4.number().min(0).max(1),
  category: z4.string().nullable(),
  status: insightStatusEnum.default("pending"),
  acknowledged_by: z4.string().uuid().nullable(),
  acknowledged_at: z4.string().datetime().nullable(),
  source_data: z4.record(z4.unknown()).nullable(),
  created_at: z4.string().datetime(),
  updated_at: z4.string().datetime()
});
var acknowledgeInsightSchema = z4.object({
  status: z4.enum(["acknowledged", "dismissed"])
});
var createInsightSchema = z4.object({
  patient_id: z4.string().uuid(),
  organization_id: z4.string().uuid(),
  report_id: z4.string().uuid().nullable().optional(),
  summary_id: z4.string().uuid().nullable().optional(),
  title: z4.string().min(1, "Titulo e obrigatorio"),
  description: z4.string(),
  severity: insightSeverityEnum,
  confidence: z4.number().min(0).max(1),
  category: z4.string().nullable().optional(),
  source_data: z4.record(z4.unknown()).nullable().optional()
});

// src/schemas/professional.schema.ts
import { z as z5 } from "zod";
var professionalRoleEnum = z5.enum(["admin", "professional"]);
var professionalStatusEnum = z5.enum(["active", "inactive", "pending"]);
var professionalSchema = z5.object({
  id: z5.string().uuid(),
  user_id: z5.string().uuid(),
  organization_id: z5.string().uuid(),
  name: z5.string().min(1, "Nome e obrigatorio"),
  email: z5.string().email("Email invalido"),
  phone: z5.string().nullable(),
  role: professionalRoleEnum.default("professional"),
  status: professionalStatusEnum.default("active"),
  specialty: z5.string().nullable(),
  registration_number: z5.string().nullable(),
  created_at: z5.string().datetime(),
  updated_at: z5.string().datetime()
});
var createProfessionalSchema = z5.object({
  organization_id: z5.string().uuid(),
  name: z5.string().min(1, "Nome e obrigatorio"),
  email: z5.string().email("Email invalido"),
  phone: z5.string().nullable().optional(),
  role: professionalRoleEnum.default("professional"),
  specialty: z5.string().nullable().optional(),
  registration_number: z5.string().nullable().optional()
});
var updateProfessionalSchema = z5.object({
  name: z5.string().min(1, "Nome e obrigatorio").optional(),
  phone: z5.string().nullable().optional(),
  role: professionalRoleEnum.optional(),
  status: professionalStatusEnum.optional(),
  specialty: z5.string().nullable().optional(),
  registration_number: z5.string().nullable().optional()
});

// src/schemas/record.schema.ts
import { z as z6 } from "zod";
var recordVisibilityEnum = z6.enum(["private", "shared", "family"]);
var recordStatusEnum = z6.enum(["active", "deleted"]);
var attachmentTypeEnum = z6.enum(["image", "document", "audio"]);
var transcriptionStatusEnum = z6.enum([
  "pending",
  "completed",
  "failed"
]);
var familyShareStatusEnum = z6.enum(["pending", "sent", "failed"]).nullable();
var auditActionEnum = z6.enum([
  "created",
  "edited",
  "deleted",
  "visibility_changed",
  "family_shared"
]);
var patientRecordSchema = z6.object({
  id: z6.string().uuid(),
  patient_id: z6.string().uuid(),
  author_id: z6.string().uuid(),
  organization_id: z6.string().uuid(),
  content: z6.string().min(1, "Conteudo e obrigatorio"),
  visibility: recordVisibilityEnum,
  status: recordStatusEnum,
  record_date: z6.string().datetime().optional(),
  family_summary_text: z6.string().nullable().optional(),
  family_summary_approved_text: z6.string().nullable().optional(),
  family_share_status: z6.string().nullable().optional(),
  family_share_sent_at: z6.string().nullable().optional(),
  created_at: z6.string().datetime(),
  updated_at: z6.string().datetime()
});
var recordAttachmentSchema = z6.object({
  id: z6.string().uuid(),
  record_id: z6.string().uuid(),
  file_type: attachmentTypeEnum,
  file_path: z6.string(),
  original_name: z6.string(),
  file_size: z6.number().int().positive(),
  mime_type: z6.string(),
  transcription: z6.string().nullable(),
  transcription_status: transcriptionStatusEnum.nullable(),
  created_at: z6.string().datetime()
});
var tagSchema = z6.object({
  id: z6.string().uuid(),
  name: z6.string().min(1, "Nome da tag e obrigatorio"),
  color: z6.string().nullable(),
  created_at: z6.string().datetime(),
  updated_at: z6.string().datetime()
});
var recordAuditLogSchema = z6.object({
  id: z6.string().uuid(),
  record_id: z6.string().uuid(),
  action: auditActionEnum,
  actor_id: z6.string().uuid(),
  metadata: z6.record(z6.unknown()).nullable(),
  created_at: z6.string().datetime()
});
var patientRecordWithRelationsSchema = patientRecordSchema.extend({
  attachments: z6.array(recordAttachmentSchema).optional(),
  tags: z6.array(tagSchema).optional(),
  author: z6.object({
    id: z6.string().uuid(),
    full_name: z6.string(),
    email: z6.string()
  }).optional()
});
var createRecordInputSchema = z6.object({
  patient_id: z6.string().uuid(),
  content: z6.string().min(1, "Conteudo e obrigatorio"),
  visibility: recordVisibilityEnum.default("shared"),
  record_date: z6.string().datetime().optional(),
  tag_ids: z6.array(z6.string().uuid()).optional()
});
var updateRecordInputSchema = z6.object({
  content: z6.string().min(1, "Conteudo e obrigatorio").optional(),
  visibility: recordVisibilityEnum.optional(),
  record_date: z6.string().datetime().optional(),
  tag_ids: z6.array(z6.string().uuid()).optional()
});
var listRecordsParamsSchema = z6.object({
  patient_id: z6.string().uuid(),
  search: z6.string().optional(),
  tag_ids: z6.array(z6.string().uuid()).optional(),
  file_type: attachmentTypeEnum.optional(),
  page: z6.number().int().positive().default(1),
  per_page: z6.number().int().positive().max(50).default(20)
});

// src/schemas/signup.schema.ts
import { z as z7 } from "zod";
var DEFAULT_BLOCKED_EMAIL_DOMAINS = [
  "mailinator.com",
  "tempmail.com",
  "10minutemail.com",
  "guerrillamail.com",
  "yopmail.com",
  "trashmail.com"
];
var passwordSchema = z7.string().min(8, "A senha deve ter pelo menos 8 caracteres").max(72, "A senha deve ter no m\xE1ximo 72 caracteres").regex(/[A-Z]/, "A senha precisa de pelo menos 1 letra mai\xFAscula").regex(/[0-9]/, "A senha precisa de pelo menos 1 n\xFAmero");
var signupSchema = z7.object({
  organization_name: z7.string().trim().min(2, "Informe o nome da organiza\xE7\xE3o").max(120),
  full_name: z7.string().trim().min(2, "Informe seu nome").max(100),
  email: z7.string().trim().toLowerCase().email("E-mail invalido"),
  password: passwordSchema,
  whatsapp: z7.string().trim().regex(
    /^\(?\d{2}\)?\s?9?\d{4}-?\d{4}$/,
    "WhatsApp invalido (use DDD + numero)"
  ).optional().or(z7.literal("")),
  terms_accepted: z7.literal(true, {
    errorMap: () => ({ message: "E necessario aceitar os termos de uso" })
  }),
  /** Anti-bot: deve vir vazio. Se preenchido, o backend retorna 200 fake. */
  website: z7.string().max(0).optional().or(z7.literal("")),
  source: z7.enum(["direct", "landing", "indicacao", "prospeccao"]).optional(),
  utm: z7.object({
    source: z7.string().max(64).optional(),
    medium: z7.string().max(64).optional(),
    campaign: z7.string().max(64).optional(),
    term: z7.string().max(64).optional(),
    content: z7.string().max(64).optional()
  }).optional(),
  lead_id: z7.string().uuid().optional()
}).superRefine((val, ctx) => {
  const domain = val.email.split("@")[1]?.toLowerCase();
  if (domain && DEFAULT_BLOCKED_EMAIL_DOMAINS.includes(domain)) {
    ctx.addIssue({ code: z7.ZodIssueCode.custom, path: ["email"], message: "Dominio de e-mail nao permitido" });
  }
});
var SIGNUP_CODES = {
  EMAIL_IN_USE: "EMAIL_IN_USE",
  SIGNUP_DISABLED: "SIGNUP_DISABLED",
  DAILY_CAP: "DAILY_CAP"
};

// src/schemas/super-admin/index.ts
var super_admin_exports = {};
__export(super_admin_exports, {
  accountStatusSchema: () => accountStatusSchema,
  acknowledgeAlertSchema: () => acknowledgeAlertSchema,
  actorTypeSchema: () => actorTypeSchema,
  addUserOrganizationSchema: () => addUserOrganizationSchema,
  alertSchema: () => alertSchema,
  alertSeveritySchema: () => alertSeveritySchema,
  alertStatusSchema: () => alertStatusSchema,
  alertTypeSchema: () => alertTypeSchema,
  auditActionTypeSchema: () => auditActionTypeSchema,
  auditLogSchema: () => auditLogSchema,
  billingIntervalSchema: () => billingIntervalSchema,
  blockUserSchema: () => blockUserSchema,
  cancelSubscriptionSchema: () => cancelSubscriptionSchema,
  changePlanSchema: () => changePlanSchema,
  configCategorySchema: () => configCategorySchema,
  configSchema: () => configSchema,
  configValueTypeSchema: () => configValueTypeSchema,
  createAlertSchema: () => createAlertSchema,
  createOrganizationSchema: () => createOrganizationSchema,
  createPlanSchema: () => createPlanSchema,
  createSubscriptionSchema: () => createSubscriptionSchema,
  dashboardMetricsQuerySchema: () => dashboardMetricsQuerySchema,
  dashboardMetricsResponseSchema: () => dashboardMetricsResponseSchema,
  dashboardTrendsQuerySchema: () => dashboardTrendsQuerySchema,
  dashboardTrendsResponseSchema: () => dashboardTrendsResponseSchema,
  dateRangeBaseSchema: () => dateRangeBaseSchema,
  dateRangeSchema: () => dateRangeSchema,
  deleteOrganizationSchema: () => deleteOrganizationSchema,
  exportAuditLogsQuerySchema: () => exportAuditLogsQuerySchema,
  impersonateUserSchema: () => impersonateUserSchema,
  invoiceSchema: () => invoiceSchema,
  invoiceStatusSchema: () => invoiceStatusSchema,
  listAlertsQuerySchema: () => listAlertsQuerySchema,
  listAuditLogsQuerySchema: () => listAuditLogsQuerySchema,
  listConfigQuerySchema: () => listConfigQuerySchema,
  listOrganizationsQuerySchema: () => listOrganizationsQuerySchema,
  listPaymentsQuerySchema: () => listPaymentsQuerySchema,
  listUsersQuerySchema: () => listUsersQuerySchema,
  moneySchema: () => moneySchema,
  organizationDetailSchema: () => organizationDetailSchema,
  organizationListItemSchema: () => organizationListItemSchema,
  paginatedResponseSchema: () => paginatedResponseSchema,
  paginationSchema: () => paginationSchema,
  paymentSchema: () => paymentSchema,
  paymentStatusSchema: () => paymentStatusSchema,
  periodSchema: () => periodSchema,
  planFeaturesSchema: () => planFeaturesSchema,
  planLimitsSchema: () => planLimitsSchema,
  planSchema: () => planSchema,
  reactivateOrganizationSchema: () => reactivateOrganizationSchema,
  reactivateSubscriptionSchema: () => reactivateSubscriptionSchema,
  refundPaymentSchema: () => refundPaymentSchema,
  resolveAlertSchema: () => resolveAlertSchema,
  sortSchema: () => sortSchema,
  stripeSubscriptionStatusSchema: () => stripeSubscriptionStatusSchema,
  subscriptionRecordStatusSchema: () => subscriptionRecordStatusSchema,
  subscriptionSchema: () => subscriptionSchema,
  subscriptionStatusSchema: () => subscriptionStatusSchema,
  suspendOrganizationSchema: () => suspendOrganizationSchema,
  trendDatapointSchema: () => trendDatapointSchema,
  unblockUserSchema: () => unblockUserSchema,
  updateConfigSchema: () => updateConfigSchema,
  updateOrganizationSchema: () => updateOrganizationSchema,
  updatePlanSchema: () => updatePlanSchema,
  updateUserOrganizationSchema: () => updateUserOrganizationSchema,
  updateUserSchema: () => updateUserSchema,
  userRoleSchema: () => userRoleSchema,
  userSchema: () => userSchema,
  uuidSchema: () => uuidSchema
});

// src/schemas/super-admin/common.ts
import { z as z8 } from "zod";
var paginationSchema = z8.object({
  page: z8.coerce.number().int().min(1).default(1),
  page_size: z8.coerce.number().int().min(1).max(100).default(50)
});
var sortSchema = z8.object({
  sort_by: z8.string().optional(),
  sort_order: z8.enum(["asc", "desc"]).default("desc")
});
var dateRangeBaseSchema = z8.object({
  start_date: z8.string().datetime().optional(),
  end_date: z8.string().datetime().optional()
});
var dateRangeSchema = dateRangeBaseSchema.refine(
  (data) => {
    if (data.start_date && data.end_date) {
      return new Date(data.start_date) <= new Date(data.end_date);
    }
    return true;
  },
  { message: "start_date must be before or equal to end_date" }
);
var periodSchema = z8.enum(["7d", "30d", "90d", "12m", "custom"]).default("30d");
var moneySchema = z8.object({
  amount_cents: z8.number().int().min(0),
  currency: z8.enum(["BRL", "USD"]).default("BRL")
});
var uuidSchema = z8.string().uuid();
var paginatedResponseSchema = (itemSchema) => z8.object({
  data: z8.array(itemSchema),
  pagination: z8.object({
    page: z8.number(),
    page_size: z8.number(),
    total: z8.number(),
    total_pages: z8.number()
  })
});

// src/schemas/super-admin/dashboard.ts
import { z as z9 } from "zod";
var dashboardMetricsQuerySchema = z9.object({
  period: periodSchema
}).merge(dateRangeBaseSchema.partial());
var dashboardMetricsResponseSchema = z9.object({
  revenue: z9.object({
    mrr_cents: z9.number(),
    arr_cents: z9.number(),
    mrr_change_percent: z9.number()
  }),
  organizations: z9.object({
    total: z9.number(),
    trial: z9.number(),
    paying: z9.number(),
    churned: z9.number(),
    suspended: z9.number(),
    new_this_period: z9.number(),
    trial_converting_soon: z9.number()
  }),
  users: z9.object({
    total: z9.number(),
    active_30d: z9.number(),
    new_this_period: z9.number()
  }),
  churn: z9.object({
    rate_percent: z9.number(),
    churned_this_period: z9.number(),
    at_risk_count: z9.number()
  }),
  engagement: z9.object({
    avg_score: z9.number(),
    high_engagement: z9.number(),
    low_engagement: z9.number()
  }),
  alerts: z9.object({
    critical: z9.number(),
    warning: z9.number(),
    info: z9.number(),
    unread: z9.number()
  }),
  calculated_at: z9.string().datetime()
});
var dashboardTrendsQuerySchema = z9.object({
  metric: z9.enum(["mrr", "organizations", "users", "churn"]),
  period: periodSchema,
  granularity: z9.enum(["day", "week", "month"]).optional()
});
var trendDatapointSchema = z9.object({
  date: z9.string(),
  value: z9.number()
});
var dashboardTrendsResponseSchema = z9.object({
  metric: z9.string(),
  period: z9.string(),
  granularity: z9.string(),
  datapoints: z9.array(trendDatapointSchema)
});

// src/schemas/super-admin/organization.ts
import { z as z10 } from "zod";
var subscriptionStatusSchema = z10.enum([
  "none",
  "trial",
  "active",
  "past_due",
  "canceled",
  "suspended"
]);
var accountStatusSchema = z10.enum(["active", "suspended"]);
var listOrganizationsQuerySchema = paginationSchema.merge(sortSchema).extend({
  status: subscriptionStatusSchema.or(z10.literal("all")).optional(),
  search: z10.string().min(1).max(100).optional(),
  engagement_min: z10.coerce.number().int().min(0).max(100).optional(),
  engagement_max: z10.coerce.number().int().min(0).max(100).optional(),
  trial_expiring_days: z10.coerce.number().int().min(1).max(30).optional()
});
var organizationListItemSchema = z10.object({
  id: uuidSchema,
  name: z10.string(),
  slug: z10.string(),
  billing_email: z10.string().email().nullable(),
  subscription_status: subscriptionStatusSchema,
  account_status: accountStatusSchema,
  trial_ends_at: z10.string().datetime().nullable(),
  trial_days_remaining: z10.number().int().nullable(),
  created_at: z10.string().datetime(),
  last_activity_at: z10.string().datetime().nullable(),
  metrics: z10.object({
    active_members: z10.number().int(),
    active_patients: z10.number().int(),
    reports_30d: z10.number().int(),
    engagement_score: z10.number()
  }),
  subscription: z10.object({
    plan_name: z10.string().nullable(),
    billing_interval: z10.enum(["monthly", "yearly"]).nullable(),
    current_period_end: z10.string().datetime().nullable()
  }).nullable(),
  alerts: z10.object({
    critical: z10.number().int(),
    warning: z10.number().int()
  })
});
var organizationDetailSchema = organizationListItemSchema.extend({
  billing_name: z10.string().nullable(),
  stripe_customer_id: z10.string().nullable(),
  suspended_at: z10.string().datetime().nullable(),
  suspension_reason: z10.string().nullable(),
  updated_at: z10.string().datetime()
});
var createOrganizationSchema = z10.object({
  name: z10.string().min(2).max(100),
  slug: z10.string().min(2).max(50).regex(/^[a-z0-9-]+$/).optional(),
  billing_email: z10.string().email(),
  billing_name: z10.string().max(100).optional(),
  plan_id: uuidSchema.optional(),
  skip_trial: z10.boolean().default(false),
  trial_days: z10.number().int().min(1).max(90).optional(),
  admin_email: z10.string().email(),
  admin_name: z10.string().min(2).max(100),
  send_invite: z10.boolean().default(true)
});
var updateOrganizationSchema = z10.object({
  name: z10.string().min(2).max(100).optional(),
  billing_email: z10.string().email().optional(),
  billing_name: z10.string().max(100).optional(),
  settings: z10.record(z10.unknown()).optional()
});
var suspendOrganizationSchema = z10.object({
  reason: z10.string().min(10).max(500),
  notify_admins: z10.boolean().default(true)
});
var reactivateOrganizationSchema = z10.object({
  reason: z10.string().max(500).optional(),
  notify_admins: z10.boolean().default(true)
});
var deleteOrganizationSchema = z10.object({
  confirm_name: z10.string().min(2).max(100),
  reason: z10.string().max(500).optional()
});

// src/schemas/super-admin/plan.ts
import { z as z11 } from "zod";
var planLimitsSchema = z11.object({
  max_units: z11.number().int().min(1).nullable().optional(),
  max_professionals: z11.number().int().min(1).max(1e3),
  max_patients: z11.number().int().min(1).max(1e5),
  max_reports_per_month: z11.number().int().min(1).nullable().optional(),
  max_storage_mb: z11.number().int().min(100).nullable().optional()
});
var planFeaturesSchema = z11.array(z11.string().min(1).max(50));
var createPlanSchema = z11.object({
  name: z11.string().min(2).max(50),
  slug: z11.string().min(2).max(30).regex(/^[a-z0-9-]+$/).optional(),
  description: z11.string().max(500).optional(),
  price_monthly_cents: z11.number().int().min(0),
  price_yearly_cents: z11.number().int().min(0),
  currency: z11.enum(["BRL", "USD"]).default("BRL"),
  billing_model: z11.enum(["fixed", "custom"]).default("fixed"),
  account_scope: z11.string().min(2).max(80).default("Conta cl\xEDnica"),
  max_units: z11.number().int().min(1).nullable().optional(),
  max_professionals: z11.number().int().min(1).max(1e3),
  max_patients: z11.number().int().min(1).max(1e5),
  max_reports_per_month: z11.number().int().min(1).nullable().optional(),
  max_storage_mb: z11.number().int().min(100).nullable().optional(),
  features: planFeaturesSchema.default([]),
  display_order: z11.number().int().min(0).default(0),
  is_highlighted: z11.boolean().default(false),
  badge_text: z11.string().max(20).nullable().optional(),
  is_public: z11.boolean().default(true),
  create_stripe_product: z11.boolean().default(true)
}).refine((data) => data.price_yearly_cents <= data.price_monthly_cents * 12, {
  message: "Yearly price must not exceed monthly price * 12"
});
var updatePlanSchema = z11.object({
  name: z11.string().min(2).max(50).optional(),
  description: z11.string().max(500).optional(),
  price_monthly_cents: z11.number().int().min(0).optional(),
  price_yearly_cents: z11.number().int().min(0).optional(),
  billing_model: z11.enum(["fixed", "custom"]).optional(),
  account_scope: z11.string().min(2).max(80).optional(),
  max_units: z11.number().int().min(1).nullable().optional(),
  max_professionals: z11.number().int().min(1).max(1e3).optional(),
  max_patients: z11.number().int().min(1).max(1e5).optional(),
  max_reports_per_month: z11.number().int().min(1).nullable().optional(),
  features: planFeaturesSchema.optional(),
  display_order: z11.number().int().min(0).optional(),
  is_highlighted: z11.boolean().optional(),
  badge_text: z11.string().max(20).nullable().optional(),
  is_public: z11.boolean().optional()
});
var planSchema = z11.object({
  id: uuidSchema,
  name: z11.string(),
  slug: z11.string(),
  description: z11.string().nullable(),
  price_monthly_cents: z11.number(),
  price_yearly_cents: z11.number(),
  currency: z11.string(),
  billing_model: z11.enum(["fixed", "custom"]).default("fixed"),
  account_scope: z11.string(),
  stripe_product_id: z11.string().nullable(),
  stripe_price_monthly_id: z11.string().nullable(),
  stripe_price_yearly_id: z11.string().nullable(),
  limits: planLimitsSchema,
  features: planFeaturesSchema,
  display_order: z11.number(),
  is_highlighted: z11.boolean(),
  badge_text: z11.string().nullable(),
  is_active: z11.boolean(),
  is_public: z11.boolean(),
  subscriber_count: z11.number(),
  created_at: z11.string().datetime(),
  updated_at: z11.string().datetime()
});

// src/schemas/super-admin/subscription.ts
import { z as z12 } from "zod";
var billingIntervalSchema = z12.enum(["monthly", "yearly"]);
var stripeSubscriptionStatusSchema = z12.enum([
  "trialing",
  "active",
  "past_due",
  "canceled",
  "unpaid",
  "incomplete",
  "incomplete_expired"
]);
var subscriptionRecordStatusSchema = z12.enum([
  "trialing",
  "active",
  "past_due",
  "canceled",
  "expired",
  "incomplete",
  "paused"
]);
var createSubscriptionSchema = z12.object({
  organization_id: uuidSchema,
  plan_id: uuidSchema,
  billing_cycle: billingIntervalSchema,
  trial_days: z12.number().int().min(0).max(90).optional()
});
var changePlanSchema = z12.object({
  new_plan_id: uuidSchema,
  prorate: z12.boolean().default(true),
  billing_interval: billingIntervalSchema.optional(),
  effective_date: z12.enum(["now", "next_period"]).default("now")
});
var cancelSubscriptionSchema = z12.object({
  reason: z12.string().max(500).optional(),
  cancel_immediately: z12.boolean().default(false),
  notify_org: z12.boolean().default(true)
});
var reactivateSubscriptionSchema = z12.object({
  plan_id: uuidSchema.optional()
});
var subscriptionSchema = z12.object({
  id: uuidSchema,
  organization_id: uuidSchema,
  organization_name: z12.string().optional(),
  plan_id: uuidSchema,
  plan_name: z12.string().optional(),
  status: subscriptionRecordStatusSchema,
  billing_cycle: billingIntervalSchema,
  trial_start: z12.string().datetime().nullable(),
  trial_end: z12.string().datetime().nullable(),
  current_period_start: z12.string().datetime(),
  current_period_end: z12.string().datetime(),
  cancel_at_period_end: z12.boolean(),
  canceled_at: z12.string().datetime().nullable(),
  stripe_subscription_id: z12.string().nullable(),
  stripe_customer_id: z12.string().nullable(),
  created_at: z12.string().datetime(),
  updated_at: z12.string().datetime()
});

// src/schemas/super-admin/payment.ts
import { z as z13 } from "zod";
var paymentStatusSchema = z13.enum([
  "pending",
  "processing",
  "succeeded",
  "failed",
  "refunded",
  "partially_refunded",
  "disputed"
]);
var listPaymentsQuerySchema = paginationSchema.merge(dateRangeBaseSchema.partial()).extend({
  organization_id: uuidSchema.optional(),
  status: paymentStatusSchema.optional()
});
var refundPaymentSchema = z13.object({
  amount_cents: z13.number().int().min(1).optional(),
  reason: z13.string().min(10).max(500)
});
var paymentSchema = z13.object({
  id: uuidSchema,
  organization_id: uuidSchema,
  organization_name: z13.string().optional(),
  subscription_id: z13.string().nullable(),
  plan_name: z13.string().optional(),
  amount: z13.number(),
  currency: z13.string(),
  status: paymentStatusSchema,
  payment_method: z13.string().nullable(),
  stripe_payment_intent_id: z13.string().nullable(),
  stripe_invoice_id: z13.string().nullable(),
  description: z13.string().nullable(),
  paid_at: z13.string().datetime().nullable(),
  failure_reason: z13.string().nullable(),
  refunded: z13.boolean(),
  refund_amount: z13.number().nullable(),
  refunded_at: z13.string().datetime().nullable(),
  metadata: z13.record(z13.unknown()).optional(),
  created_at: z13.string().datetime()
});
var invoiceStatusSchema = z13.enum(["draft", "open", "paid", "void", "uncollectible"]);
var invoiceSchema = z13.object({
  id: uuidSchema,
  invoice_number: z13.string(),
  organization: z13.object({
    id: uuidSchema,
    name: z13.string()
  }),
  status: invoiceStatusSchema,
  subtotal_cents: z13.number(),
  tax_cents: z13.number(),
  discount_cents: z13.number(),
  total_cents: z13.number(),
  amount_paid_cents: z13.number(),
  amount_due_cents: z13.number(),
  currency: z13.string(),
  invoice_date: z13.string().datetime(),
  due_date: z13.string().datetime().nullable(),
  paid_at: z13.string().datetime().nullable(),
  pdf_url: z13.string().nullable(),
  hosted_invoice_url: z13.string().nullable(),
  line_items: z13.array(
    z13.object({
      description: z13.string(),
      quantity: z13.number(),
      unit_amount_cents: z13.number()
    })
  ),
  created_at: z13.string().datetime()
});

// src/schemas/super-admin/user.ts
import { z as z14 } from "zod";
var userRoleSchema = z14.enum(["admin", "professional", "assistant"]);
var listUsersQuerySchema = paginationSchema.extend({
  search: z14.string().min(1).max(100).optional(),
  organization_id: uuidSchema.optional(),
  role: userRoleSchema.optional(),
  is_blocked: z14.coerce.boolean().optional()
});
var updateUserSchema = z14.object({
  email: z14.string().email().optional(),
  full_name: z14.string().min(2).max(100).optional(),
  phone: z14.string().max(30).nullable().optional()
});
var addUserOrganizationSchema = z14.object({
  organization_id: uuidSchema,
  role: userRoleSchema
});
var updateUserOrganizationSchema = z14.object({
  role: userRoleSchema.optional(),
  is_active: z14.boolean().optional(),
  permissions: z14.record(z14.boolean()).optional()
});
var impersonateUserSchema = z14.object({
  organization_id: uuidSchema,
  reason: z14.string().min(10).max(500)
});
var blockUserSchema = z14.object({
  reason: z14.string().min(10).max(500),
  notify_user: z14.boolean().default(true)
});
var unblockUserSchema = z14.object({
  reason: z14.string().max(500).optional()
});
var userSchema = z14.object({
  profile_id: uuidSchema,
  full_name: z14.string(),
  email: z14.string().email(),
  phone: z14.string().nullable(),
  created_at: z14.string().datetime(),
  last_sign_in_at: z14.string().datetime().nullable(),
  has_auth_account: z14.boolean(),
  email_confirmed_at: z14.string().datetime().nullable(),
  is_blocked: z14.boolean(),
  blocked_at: z14.string().datetime().nullable().optional(),
  blocked_reason: z14.string().nullable().optional(),
  is_super_admin: z14.boolean(),
  organizations: z14.array(
    z14.object({
      id: uuidSchema,
      name: z14.string(),
      role: userRoleSchema,
      is_active: z14.boolean(),
      permissions: z14.record(z14.boolean()).optional()
    })
  )
});

// src/schemas/super-admin/alert.ts
import { z as z15 } from "zod";
var alertTypeSchema = z15.enum([
  "trial_expiring",
  "trial_expired",
  "low_activity",
  "engagement_drop",
  "payment_failed",
  "payment_past_due",
  "churn_risk",
  "limit_approaching",
  "limit_reached",
  "system_error"
]);
var alertSeveritySchema = z15.enum(["info", "warning", "critical"]);
var alertStatusSchema = z15.enum(["active", "acknowledged", "resolved", "dismissed"]);
var listAlertsQuerySchema = paginationSchema.extend({
  severity: alertSeveritySchema.optional(),
  type: alertTypeSchema.optional(),
  status: alertStatusSchema.optional(),
  organization_id: uuidSchema.optional()
});
var createAlertSchema = z15.object({
  type: alertTypeSchema,
  severity: alertSeveritySchema,
  title: z15.string().min(1).max(200),
  message: z15.string().min(1).max(1e3),
  organization_id: uuidSchema.optional(),
  metadata: z15.record(z15.unknown()).optional()
});
var acknowledgeAlertSchema = z15.object({});
var resolveAlertSchema = z15.object({
  resolution_notes: z15.string().max(500).optional()
});
var alertSchema = z15.object({
  id: uuidSchema,
  type: z15.string(),
  severity: alertSeveritySchema,
  title: z15.string(),
  message: z15.string(),
  organization_id: uuidSchema.nullable(),
  organization_name: z15.string().nullable(),
  metadata: z15.record(z15.unknown()),
  status: alertStatusSchema,
  acknowledged_at: z15.string().datetime().nullable(),
  acknowledged_by: uuidSchema.nullable(),
  resolved_at: z15.string().datetime().nullable(),
  resolved_by: uuidSchema.nullable(),
  resolution_notes: z15.string().nullable(),
  created_at: z15.string().datetime()
});

// src/schemas/super-admin/audit-log.ts
import { z as z16 } from "zod";
var auditActionTypeSchema = z16.enum([
  // Organization
  "organization.created",
  "organization.updated",
  "organization.suspended",
  "organization.reactivated",
  "organization.deleted",
  // Subscription
  "subscription.created",
  "subscription.plan_changed",
  "subscription.canceled",
  "subscription.reactivated",
  // Payment
  "payment.created",
  "payment.succeeded",
  "payment.failed",
  "payment.refunded",
  // Plan
  "plan.created",
  "plan.updated",
  "plan.deactivated",
  // User
  "user.created",
  "user.updated",
  "user.blocked",
  "user.unblocked",
  "user.impersonated",
  // Config
  "config.updated",
  // Super admin
  "super_admin.created",
  "super_admin.updated",
  "super_admin.removed",
  // Alert
  "alert.created",
  "alert.acknowledged",
  "alert.resolved"
]);
var actorTypeSchema = z16.enum(["super_admin", "system", "webhook"]);
var listAuditLogsQuerySchema = paginationSchema.merge(dateRangeBaseSchema.partial()).extend({
  actor_id: uuidSchema.optional(),
  action: auditActionTypeSchema.optional(),
  target_type: z16.string().optional(),
  target_id: uuidSchema.optional(),
  search: z16.string().max(100).optional()
});
var exportAuditLogsQuerySchema = dateRangeBaseSchema.extend({
  format: z16.enum(["csv", "json"]).default("csv"),
  action: auditActionTypeSchema.optional()
});
var auditLogSchema = z16.object({
  id: uuidSchema,
  actor: z16.object({
    id: uuidSchema.nullable(),
    email: z16.string(),
    type: actorTypeSchema
  }),
  action: auditActionTypeSchema,
  target: z16.object({
    type: z16.string(),
    id: uuidSchema.nullable(),
    identifier: z16.string().nullable()
  }),
  description: z16.string().nullable(),
  old_values: z16.record(z16.unknown()).nullable(),
  new_values: z16.record(z16.unknown()).nullable(),
  ip_address: z16.string().nullable(),
  session_id: z16.string().nullable(),
  created_at: z16.string().datetime()
});

// src/schemas/super-admin/config.ts
import { z as z17 } from "zod";
var configValueTypeSchema = z17.enum(["string", "number", "boolean", "json", "array"]);
var configCategorySchema = z17.enum([
  "general",
  "trial",
  "billing",
  "notifications",
  "limits",
  "features"
]);
var listConfigQuerySchema = z17.object({
  category: configCategorySchema.optional()
});
var updateConfigSchema = z17.object({
  value: z17.unknown()
});
var configSchema = z17.object({
  id: uuidSchema,
  key: z17.string(),
  value: z17.unknown(),
  description: z17.string().nullable(),
  category: configCategorySchema,
  value_type: configValueTypeSchema,
  updated_at: z17.string().datetime(),
  updated_by: uuidSchema.nullable()
});

// src/utils.ts
import { format, startOfWeek, endOfWeek, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
function formatDate(date, pattern = "dd/MM/yyyy") {
  const dateObj = typeof date === "string" ? parseISO(date) : date;
  return format(dateObj, pattern, { locale: ptBR });
}
function getWeekRange(date = /* @__PURE__ */ new Date()) {
  return {
    start: startOfWeek(date, { weekStartsOn: 0 }),
    // Domingo
    end: endOfWeek(date, { weekStartsOn: 0 })
    // Sábado
  };
}
function formatPhoneNumber(phone) {
  const cleaned = phone.replace(/\D/g, "");
  if (cleaned.length === 11) {
    return `+55${cleaned}`;
  } else if (cleaned.length === 13 && cleaned.startsWith("55")) {
    return `+${cleaned}`;
  }
  return phone;
}
function extractPatientAge(birthDate) {
  const birth = parseISO(birthDate);
  const today = /* @__PURE__ */ new Date();
  const age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || monthDiff === 0 && today.getDate() < birth.getDate()) {
    return age - 1;
  }
  return age;
}
function sanitizeContent(content) {
  return content.trim().replace(/\s+/g, " ").replace(/[^\w\s\-.,!?áéíóúâêîôûàèìòùãõç]/gi, "");
}
function generateInsightId() {
  return `insight_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}
function validateWhatsAppNumber(phone) {
  const cleaned = phone.replace(/\D/g, "");
  return cleaned.length >= 10 && cleaned.length <= 15;
}

// src/constants.ts
var APP_CONFIG = {
  name: "SecBrain TEA",
  version: "1.0.0",
  description: "Sistema de Acompanhamento de Sa\xFAde via WhatsApp"
};
var REPORT_TYPES = {
  TEXT: "text",
  AUDIO: "audio",
  IMAGE: "image",
  VIDEO: "video"
};
var USER_ROLES = {
  FAMILY_MEMBER: "family_member",
  HEALTH_PROFESSIONAL: "health_professional",
  ADMIN: "admin"
};
var PATIENT_STATUS = {
  ACTIVE: "active",
  INACTIVE: "inactive",
  DISCHARGED: "discharged"
};
var INSIGHT_SEVERITY = {
  LOW: "low",
  MEDIUM: "medium",
  HIGH: "high",
  CRITICAL: "critical"
};
var MOOD_INDICATORS = {
  ENERGY: { min: 1, max: 5, labels: ["Muito baixa", "Baixa", "Normal", "Alta", "Muito alta"] },
  SLEEP: { min: 1, max: 5, labels: ["Muito ruim", "Ruim", "Regular", "Bom", "Excelente"] },
  APPETITE: { min: 1, max: 5, labels: ["Nenhum", "Pouco", "Normal", "Bom", "Excelente"] },
  SOCIAL: { min: 1, max: 5, labels: ["Isolado", "Retra\xEDdo", "Normal", "Soci\xE1vel", "Muito soci\xE1vel"] }
};
var SYSTEM_MESSAGES = {
  WELCOME: "\u{1F3E5} Bem-vindo ao SecBrain TEA!\n\nSistema de relat\xF3rios de sa\xFAde ativado.\n\nVoc\xEA pode enviar:\n\u{1F4DD} Relat\xF3rios em texto\n\u{1F5E3}\uFE0F Mensagens de \xE1udio\n\u{1F4F7} Fotos com descri\xE7\xE3o\n\nSuas informa\xE7\xF5es s\xE3o importantes para o cuidado especializado.",
  CONFIRMATION: "\u2705 Relat\xF3rio recebido!\n\nObrigado por manter-nos informados sobre o cuidado. Sua dedica\xE7\xE3o faz toda a diferen\xE7a.",
  REMINDER: "\u{1F4DD} Lembrete amig\xE1vel!\n\nOl\xE1! Notamos que ainda n\xE3o recebemos o relat\xF3rio di\xE1rio hoje.\n\nPoderia nos enviar uma atualiza\xE7\xE3o quando poss\xEDvel? Qualquer informa\xE7\xE3o \xE9 valiosa para o acompanhamento.",
  ERROR: "\u274C Ops! Algo deu errado.\n\nTente novamente em alguns minutos. Se o problema persistir, entre em contato com nossa equipe.",
  UNAUTHORIZED: "\u{1F6AB} Acesso n\xE3o autorizado.\n\nEste n\xFAmero n\xE3o est\xE1 cadastrado para envio de relat\xF3rios. Entre em contato com a equipe de sa\xFAde para mais informa\xE7\xF5es."
};
var WEBHOOK_EVENTS = {
  MESSAGE_UPSERT: "messages.upsert",
  MESSAGE_UPDATE: "messages.update",
  CONNECTION_UPDATE: "connection.update"
};
var AI_PROCESSING = {
  BATCH_SIZE: 10,
  MAX_RETRIES: 3,
  TIMEOUT_MS: 3e4,
  CONFIDENCE_THRESHOLD: 0.7
};
export {
  AI_PROCESSING,
  APP_CONFIG,
  DEFAULT_BLOCKED_EMAIL_DOMAINS,
  INSIGHT_SEVERITY,
  MOOD_INDICATORS,
  PATIENT_STATUS,
  REPORT_TYPES,
  SIGNUP_CODES,
  SYSTEM_MESSAGES,
  USER_ROLES,
  WEBHOOK_EVENTS,
  acknowledgeInsightSchema,
  attachmentTypeEnum,
  auditActionEnum,
  createFamilyMemberSchema,
  createInsightSchema,
  createPatientSchema,
  createProfessionalSchema,
  createRecordInputSchema,
  createReportSchema,
  dailyReportSchema,
  extractPatientAge,
  familyMemberSchema,
  familyRelationshipEnum,
  familyShareStatusEnum,
  formatDate,
  formatPhoneNumber,
  generateInsightId,
  getWeekRange,
  insightSeverityEnum,
  insightStatusEnum,
  keyInsightSchema,
  listRecordsParamsSchema,
  patientRecordSchema,
  patientRecordWithRelationsSchema,
  patientSchema,
  patientStatusEnum,
  professionalRoleEnum,
  professionalSchema,
  professionalStatusEnum,
  recordAttachmentSchema,
  recordAuditLogSchema,
  recordStatusEnum,
  recordVisibilityEnum,
  reportTypeEnum,
  sanitizeContent,
  sentimentEnum,
  signupSchema,
  super_admin_exports as superAdmin,
  tagSchema,
  transcriptionStatusEnum,
  updatePatientSchema,
  updateProfessionalSchema,
  updateRecordInputSchema,
  validateWhatsAppNumber,
  weeklySummarySchema
};
//# sourceMappingURL=index.mjs.map